import { zendeskHttpRequest } from "./zendeskHttpRequest.ts";
import { CancelManager } from "../cancelManager.ts";

interface PaginatedResponse<T> {
  [key: string]: T[] | string | null;

  // @ts-ignore
  links: {
    next: string;
  };
  // @ts-ignore
  meta: {
    has_more: boolean;
  };
}

/**
 * Zendesk API からカーソルページネーション対応のデータを取得する関数。全てのデータを取得するまでAPIコールを繰り返す。
 *
 * @param {string} initialEndpoint - 最初にコールするAPI エンドポイント
 * @param {string} token - API認証トークン
 * @param {string} brandUrl - Zendesk のブランド URL
 * @param {string} dataKey - レスポンス内のデータキー（配列のキー）
 * @returns {Promise<T[]>} - すべてのページのデータを結合した配列
 * @see　ページネーションについてのZendeskドキュメント https://developer.zendesk.com/api-reference/introduction/pagination/
 */
export const getPaginatedData = async <T>(
  initialEndpoint: string,
  token: string,
  brandUrl: string,
  dataKey: string,
  onProgress?: (fetchedCount: number) => void,
  delayMs: number = 200,
): Promise<T[]> => {
  let allData: T[] = [];

  const separator = initialEndpoint.includes("?") ? "&" : "?";
  let nextPage: string | null = initialEndpoint + separator + "page[size]=100";

  let hasMore: boolean = true;
  const cancelManager = CancelManager.getInstance();

  while (hasMore) {
    cancelManager.checkCancelled();

    const response: PaginatedResponse<T> = await zendeskHttpRequest<
      PaginatedResponse<T>
    >(nextPage, token, "GET", brandUrl);

    allData = allData.concat(response[dataKey] as T[]);
    onProgress?.(allData.length);
    hasMore = response.meta.has_more;
    nextPage = response.links.next;

    if (hasMore) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  return allData;
};
